/**
 * FallbackChainEditor — ordered fallback chain for one model (TRA-923).
 *
 * The chain is tried left to right (rendered top to bottom) when the
 * primary route fails; length is unbounded. Targets come from the
 * fallback-options endpoint: public catalog slugs (resolved via the
 * gateway's `__public_routes`) and private upstream ids. A private
 * upstream is only pickable when it already carries a deployment keyed by
 * THIS model's canonical slug — without that slot the fallback 404s at
 * exactly the moment the primary is down, which is the one failure mode
 * this feature exists to prevent.
 *
 * The gateway enforces the same rules server-side (400s name the value);
 * `validateFallbackChain` mirrors them so the form blocks before the
 * round-trip.
 */

import { useState } from 'react'

import { ArrowDown, ArrowUp, ChevronsUpDown, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { FallbackOptionsResponse } from '@/api/generated/model'
import { validateFallbackChain } from '@/components/models/fallbackChain'

// Catalog slugs group under their provider namespace (`<provider>/<model>`).
// An unmapped namespace renders as its raw prefix, so a new provider is
// visible (just unpolished) rather than hidden.
const PROVIDER_HEADINGS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  fireworks: 'Fireworks AI',
  baseten: 'Baseten',
  wandb: 'W&B Inference',
}

function providerHeading(slug: string): string {
  const prefix = slug.split('/')[0] ?? slug
  return PROVIDER_HEADINGS[prefix] ?? prefix
}

function targetLabel(value: string, options: FallbackOptionsResponse): string {
  const catalog = options.catalog.find((c) => c.slug === value)
  if (catalog) return catalog.display_name
  const upstream = options.upstreams.find((u) => u.id === value)
  if (upstream) return upstream.name
  return 'Unknown target'
}

function moved(chain: string[], index: number, delta: -1 | 1): string[] {
  const a = chain[index]
  const b = chain[index + delta]
  if (a === undefined || b === undefined) return chain
  const next = [...chain]
  next[index] = b
  next[index + delta] = a
  return next
}

interface FallbackChainEditorProps {
  chain: string[]
  onChange: (chain: string[]) => void
  options: FallbackOptionsResponse
  /** The route value the chain must not repeat — the stored primary, or
   * the repoint target when the same edit is switching backing. */
  effectivePrimary: string
  disabled: boolean
}

export function FallbackChainEditor({
  chain,
  onChange,
  options,
  effectivePrimary,
  disabled,
}: FallbackChainEditorProps): React.JSX.Element {
  const errors = validateFallbackChain(chain, options, effectivePrimary)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Stable provider order: first appearance in the catalog list (which the
  // API returns sorted by slug, so providers come out alphabetically).
  const providerGroups: { heading: string; items: typeof options.catalog }[] = []
  for (const c of options.catalog) {
    const heading = providerHeading(c.slug)
    const group = providerGroups.find((g) => g.heading === heading)
    if (group) {
      group.items.push(c)
    } else {
      providerGroups.push({ heading, items: [c] })
    }
  }

  function addTarget(value: string): void {
    onChange([...chain, value])
    setPickerOpen(false)
  }

  return (
    <div className="space-y-2">
      <Label>Fallback chain</Label>
      <p className="text-xs text-muted-foreground">
        Tried top to bottom when the primary fails. Requests served by a
        fallback stay 200s. The uptime wall marks those blocks amber
        instead of down.
      </p>

      {chain.length === 0 ? (
        <p className="text-sm text-muted-foreground">No fallbacks configured.</p>
      ) : (
        <ol className="space-y-1">
          {chain.map((value, index) => (
            <li
              // Index-qualified: a stale chain can contain duplicates, and
              // two identical keys would break reordering.
              key={`${index.toString()}-${value}`}
              className="flex items-center gap-2 border border-border px-2 py-1"
            >
              <span className="w-4 text-xs text-muted-foreground">{index + 1}.</span>
              <span className="min-w-0 flex-1 truncate font-mono text-sm">{value}</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {targetLabel(value, options)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Move ${value} up`}
                disabled={disabled || index === 0}
                onClick={() => { onChange(moved(chain, index, -1)) }}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Move ${value} down`}
                disabled={disabled || index === chain.length - 1}
                onClick={() => { onChange(moved(chain, index, 1)) }}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove ${value} from the chain`}
                disabled={disabled}
                onClick={() => { onChange(chain.filter((_, i) => i !== index)) }}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ol>
      )}

      {/* modal: inside the edit Dialog the body scroll-lock swallows wheel
          events on portaled content, making the list unscrollable. A modal
          popover brings its own scroll layer. */}
      <Popover modal open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={pickerOpen}
            aria-label="Add fallback target"
            disabled={disabled}
            className="w-full justify-between font-normal text-muted-foreground"
          >
            Add a fallback target…
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder="Search models and upstreams…" />
            <CommandList>
              <CommandEmpty>No matching target.</CommandEmpty>
              <CommandGroup heading="Private upstreams">
                {options.upstreams.map((u) => (
                  <CommandItem
                    key={u.id}
                    // Search matches name and id.
                    value={`${u.name} ${u.id}`}
                    disabled={
                      u.id === effectivePrimary || chain.includes(u.id) || !u.has_deployment
                    }
                    onSelect={() => { addTarget(u.id) }}
                  >
                    {u.name}
                    <span className="ml-2 font-mono text-xs opacity-60">{u.id}</span>
                    {!u.has_deployment && (
                      <span className="ml-2 text-xs opacity-60">
                        no deployment for this model
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
              {providerGroups.map((group) => (
                <CommandGroup key={group.heading} heading={group.heading}>
                  {group.items.map((c) => (
                    <CommandItem
                      key={c.slug}
                      value={`${c.display_name} ${c.slug}`}
                      disabled={c.slug === effectivePrimary || chain.includes(c.slug)}
                      onSelect={() => { addTarget(c.slug) }}
                    >
                      {c.display_name}
                      <span className="ml-2 font-mono text-xs opacity-60">{c.slug}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {errors.map((message) => (
        <p key={message} role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ))}

      {chain.length >= 3 && (
        <p className="text-xs text-warning">
          Attempts are sequential. Each failing target can wait out its own
          upstream timeout before the next is tried, so a long chain can add
          a lot of latency.
        </p>
      )}
    </div>
  )
}
